import axios from 'axios';
import { isSessionError } from './sessionErrors';

// Create an axios instance with default config
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3001/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor for adding auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling errors
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Only clear the session for genuine token/session failures — not for
    // business-logic 401s like INVALID_CREDENTIALS or INVALID_CURRENT_PASSWORD.
    const sentAuthorization = error.config?.headers?.Authorization;
    const currentToken = localStorage.getItem('token');
    // Responses from an old tab/request must not remove a newer login token.
    if (isSessionError(error) && currentToken && sentAuthorization === `Bearer ${currentToken}`) {
      localStorage.removeItem('token');
      // Also drop any preserved support token so an expired impersonation
      // session cannot leave an orphaned key behind.
      localStorage.removeItem('impersonatorToken');
      // /link-login must never be redirected away: a stale stored JWT failing
      // /auth/me on boot would otherwise discard the mail link's token before
      // the exchange runs. Dropping the dead token above is enough there.
      // /legal for the same reason in reverse: those documents must be readable
      // with no session at all (GDPR art. 12-13, and standard terms bind only
      // if they were available beforehand), so a stale token failing /auth/me
      // on boot must not bounce the reader to the login page.
      const path = window.location.pathname;
      const isPublicAuth = ['/', '/login', '/adminlogin', '/link-login', '/forgot-password', '/reset-password'].includes(path);
      if (error.config?.url !== '/auth/me' && !isPublicAuth && !path.startsWith('/legal')) {
        window.location.href = path.startsWith('/admin') || path.startsWith('/support') ? '/adminlogin' : '/login';
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
